import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  assignBadgeAlphaCode,
  callTool,
  fetchBadgeAlphaCode,
  fetchBadgeLead,
  queueBadgeAugmentation,
  type PlusLeadCapture,
} from "../api";
import { quickCreateAccount } from "../accounts/accountCreate";
import { ContactSendModal } from "../campaign-builder/ContactSendModal";
import { resolveMarketingCampaignId } from "../campaign-builder/campaignResolver";
import { type ContactRecord, PRODUCT_INTEREST_OPTIONS, type ProductInterest } from "../contact-browser/types";
import { NotesPane } from "../contacts/contactActivityPanes";
import { CompanyBrowserModal } from "../company-browser/CompanyBrowserModal";
import type { CompanyBrowserTarget } from "../company-browser/types";
import { EVENT_KEY } from "../constants";
import { ratingLineage } from "../ratings";
import { badgeSearchQuery, parseBadgePayload, type BadgeFields } from "./badgePayload";
import { BarcodeDetector as BarcodeDetectorPonyfill } from "barcode-detector/ponyfill";

type ContactHit = {
  contact_id: string;
  account_id: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone?: string | null;
  title: string | null;
  account_name: string | null;
  company: string | null;
};

type ScanPhase = "starting" | "live" | "paused" | "error";

type BadgeCaptureDraft = {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  title: string;
  company: string;
  website: string;
  address: string;
  city: string;
  state: string;
  postal: string;
  country: string;
  describes_you: string;
  marketing_opt_in: boolean;
  promo_plaud: boolean;
  product_interest: ProductInterest;
};

const CHANNEL = "badge_scan";
const EVENT_PROMO_CODE = "plaud-2026";
const EVENT_PROMO_LABEL = "PLAUD";
const DEFAULT_DESCRIBES_YOU = "Repair station / MRO";
const DESCRIBES_YOU_OPTIONS = [
  "Director of Maintenance",
  "Technician / Inspector",
  "Flight operations",
  "Repair station / MRO",
  "Aircraft management",
  "Other",
] as const;

// iOS WebKit (Safari, Brave, Edge, Chrome on iPhone — all WebKit) has no native
// BarcodeDetector. Fall back to the WASM ponyfill there; Chromium keeps the native one.
function barcodeDetectorCtor(): (typeof BarcodeDetector) | null {
  if (typeof BarcodeDetector === "function") return BarcodeDetector;
  return BarcodeDetectorPonyfill as unknown as typeof BarcodeDetector;
}

// Detect from a downscaled canvas frame. The ponyfill decodes a bitmap much faster
// than a full-resolution video element, and iOS memory stays bounded.
const DETECT_WIDTH = 640;
function frameFor(video: HTMLVideoElement, canvas: HTMLCanvasElement): HTMLCanvasElement | null {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) return null;
  const w = Math.min(DETECT_WIDTH, vw);
  const h = Math.round(vh * (w / vw));
  if (canvas.width !== w || canvas.height !== h) {
    canvas.width = w;
    canvas.height = h;
  }
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(video, 0, 0, w, h);
  return canvas;
}

function personName(row: ContactHit): string {
  return [row.first_name, row.last_name].filter(Boolean).join(" ").trim() || "—";
}

function asText(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function defaultProductInterest(): ProductInterest {
  return {
    vault: false,
    operate: false,
    shop: false,
    forge: false,
  };
}

function captureDraftFromBadge(fields: BadgeFields): BadgeCaptureDraft {
  return {
    first_name: fields.firstName ?? "",
    last_name: fields.lastName ?? "",
    email: fields.email ?? "",
    phone: fields.phone ?? "",
    title: fields.title ?? "",
    company: fields.company ?? "",
    website: fields.website ?? "",
    address: fields.address ?? "",
    city: fields.city ?? "",
    state: fields.state ?? "",
    postal: fields.postal ?? "",
    country: fields.country ?? "",
    describes_you: DEFAULT_DESCRIBES_YOU,
    marketing_opt_in: true,
    promo_plaud: true,
    product_interest: defaultProductInterest(),
  };
}

function normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}

function selectedInterests(value: ProductInterest): string[] {
  return PRODUCT_INTEREST_OPTIONS.filter(({ key }) => value[key]).map(({ label }) => label.toUpperCase());
}

function fieldsFromSdkCapture(barcode: string, payload: PlusLeadCapture): BadgeFields {
  const profile = payload.profile ?? {};
  const line2 = profile.address2?.trim();
  const line3 = profile.address3?.trim();
  return {
    raw: barcode,
    externalId: payload.lead_id?.trim() || undefined,
    firstName: profile.first_name?.trim() || undefined,
    lastName: profile.last_name?.trim() || undefined,
    name: [profile.first_name, profile.last_name].filter(Boolean).join(" ").trim() || undefined,
    email: profile.email?.trim() || undefined,
    phone: profile.phone?.trim() || undefined,
    title: profile.title?.trim() || undefined,
    company: profile.company?.trim() || undefined,
    website: profile.website?.trim() || undefined,
    address: [profile.address1, line2, line3].filter(Boolean).join(", ") || undefined,
    city: profile.city?.trim() || undefined,
    state: profile.state?.trim() || undefined,
    postal: profile.postal?.trim() || undefined,
    country: profile.country?.trim() || undefined,
  };
}

function readProductInterest(raw: ContactRecord["raw_attributes"]): ProductInterest {
  const base = defaultProductInterest();
  const attrs = raw && typeof raw === "object" && !Array.isArray(raw) ? raw : null;
  const pi = attrs?.product_interest;
  const obj = pi && typeof pi === "object" && !Array.isArray(pi) ? (pi as Record<string, unknown>) : attrs;
  if (!obj) return base;
  for (const option of PRODUCT_INTEREST_OPTIONS) {
    const value = obj[option.key];
    if (value === true || value === "true") {
      base[option.key] = true;
    }
  }
  return base;
}

function mergeIntoDraft(base: BadgeCaptureDraft, secondary: Partial<BadgeCaptureDraft>): BadgeCaptureDraft {
  const pick = (current: string, incoming?: string) => current.trim() || asText(incoming);
  return {
    ...base,
    first_name: pick(base.first_name, secondary.first_name),
    last_name: pick(base.last_name, secondary.last_name),
    email: pick(base.email, secondary.email),
    phone: pick(base.phone, secondary.phone),
    title: pick(base.title, secondary.title),
    company: pick(base.company, secondary.company),
    website: pick(base.website, secondary.website),
    address: pick(base.address, secondary.address),
    city: pick(base.city, secondary.city),
    state: pick(base.state, secondary.state),
    postal: pick(base.postal, secondary.postal),
    country: pick(base.country, secondary.country),
    describes_you: pick(base.describes_you, secondary.describes_you),
    marketing_opt_in: base.marketing_opt_in || secondary.marketing_opt_in === true,
    promo_plaud: base.promo_plaud || secondary.promo_plaud === true,
    product_interest: {
      vault: base.product_interest.vault || secondary.product_interest?.vault === true,
      operate: base.product_interest.operate || secondary.product_interest?.operate === true,
      shop: base.product_interest.shop || secondary.product_interest?.shop === true,
      forge: base.product_interest.forge || secondary.product_interest?.forge === true,
    },
  };
}

function draftFromContact(contact: ContactRecord, hint?: ContactHit): Partial<BadgeCaptureDraft> {
  return {
    first_name: asText(contact.first_name) || asText(hint?.first_name),
    last_name: asText(contact.last_name) || asText(hint?.last_name),
    email: asText(contact.email) || asText(hint?.email),
    phone: asText(contact.phone) || asText(hint?.phone),
    title: asText(contact.title) || asText(hint?.title),
    company: asText(contact.company) || asText(hint?.account_name) || asText(hint?.company),
    website: asText(contact.source_url) || asText(contact.profile_url),
    address: [contact.address_line1, contact.address_line2, contact.address_line3].filter(Boolean).join(", "),
    city: asText(contact.city),
    state: asText(contact.state_region),
    postal: asText(contact.postal_code),
    country: asText(contact.country_raw) || asText(contact.country_code),
    product_interest: readProductInterest(contact.raw_attributes),
  };
}

export function BadgeScannerModal({
  eventKey = EVENT_KEY,
  campaignKey,
  actor,
  onClose,
  onOpenCompany,
  onViewInContacts,
  onOpenContact,
}: {
  eventKey?: string;
  campaignKey: string;
  actor: string;
  onClose: () => void;
  onOpenCompany?: (target: CompanyBrowserTarget) => void;
  onViewInContacts?: (query: string) => void;
  onOpenContact?: (contactId: string) => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number>(0);
  const detectorRef = useRef<BarcodeDetector | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [phase, setPhase] = useState<ScanPhase>("starting");
  const [status, setStatus] = useState("Starting camera…");
  const [fields, setFields] = useState<BadgeFields | null>(null);
  const [capture, setCapture] = useState<BadgeCaptureDraft | null>(null);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<string | null>(null);
  const [sendHit, setSendHit] = useState<ContactHit | null>(null);
  const [marketingCampaignId, setMarketingCampaignId] = useState<string | null>(null);
  const [hits, setHits] = useState<ContactHit[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [matching, setMatching] = useState(false);
  const [matchError, setMatchError] = useState<string | null>(null);
  const [linkedMatches, setLinkedMatches] = useState<ContactHit[]>([]);
  const [selectedMatchIds, setSelectedMatchIds] = useState<string[]>([]);
  const [selectedDetails, setSelectedDetails] = useState<Record<string, ContactRecord>>({});
  const [merging, setMerging] = useState(false);
  const [savedContactId, setSavedContactId] = useState<string | null>(null);
  const [noteContact, setNoteContact] = useState<{
    contact_id: string;
    account_id: string | null;
    first_name: string;
    last_name: string;
  } | null>(null);
  const [alphaCode, setAlphaCode] = useState<string | null>(null);
  const [eventContactDisplay, setEventContactDisplay] = useState<string | null>(null);
  const [refreshingCode, setRefreshingCode] = useState(false);
  const autoSaveRaw = useRef<string | null>(null);

  useEffect(() => {
    void resolveMarketingCampaignId(campaignKey).then(setMarketingCampaignId).catch(() => undefined);
  }, [campaignKey]);

  const stopStream = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
    const stream = streamRef.current;
    streamRef.current = null;
    stream?.getTracks().forEach((track) => track.stop());
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.srcObject = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    stopStream();
    setFields(null);
    setCapture(null);
    setHits(null);
    setSearchError(null);
    setSaveError(null);
    setSaveStatus(null);
    setCopied(false);
    setMatchError(null);
    setLinkedMatches([]);
    setSelectedMatchIds([]);
    setSelectedDetails({});
    setSavedContactId(null);
    setNoteContact(null);
    setAlphaCode(null);
    setEventContactDisplay(null);
    autoSaveRaw.current = null;
    setPhase("starting");
    setStatus("Starting camera…");

    const Detector = barcodeDetectorCtor();
    if (!Detector) {
      setPhase("error");
      setStatus("QR reading is not available in this browser.");
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setPhase("error");
      setStatus("Camera is not available in this browser.");
      return;
    }

    try {
      const formats = await Detector.getSupportedFormats().catch(() => ["qr_code"]);
      const preferred = ["qr_code", "pdf417"].filter((format) => formats.includes(format));
      detectorRef.current = new Detector({ formats: preferred.length ? preferred : ["qr_code"] });
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      streamRef.current = stream;
      const video = videoRef.current;
      if (!video) {
        stream.getTracks().forEach((track) => track.stop());
        setPhase("error");
        setStatus("Camera preview failed to start.");
        return;
      }
      video.srcObject = stream;
      // iOS: play() can reject when it isn't tied to a user gesture; the stream still
      // renders because the element is autoplay+muted+playsinline, so don't fail on it.
      await video.play().catch(() => undefined);
      setPhase("live");
      setStatus("Point the camera at a badge QR code.");
    } catch (error) {
      const denied =
        error instanceof DOMException && (error.name === "NotAllowedError" || error.name === "PermissionDeniedError");
      setPhase("error");
      setStatus(denied ? "Camera permission was denied." : "Unable to open the camera.");
    }
  }, [stopStream]);

  useEffect(() => {
    void startCamera();
    return () => stopStream();
  }, [startCamera, stopStream]);

  useEffect(() => {
    if (phase !== "live") return;
    const video = videoRef.current;
    const detector = detectorRef.current;
    if (!video || !detector) return;
    let active = true;
    let lastTick = 0;
    let busy = false; // the WASM ponyfill can take >1 frame; never overlap detect() calls

    const tick = async (now: number) => {
      if (!active) return;
      rafRef.current = requestAnimationFrame(tick);
      if (busy || now - lastTick < 220) return;
      lastTick = now;
      if (video.readyState < 2) return;
      busy = true;
      try {
        const canvas = (canvasRef.current ??= document.createElement("canvas"));
        const frame = frameFor(video, canvas);
        if (!frame) return;
        const found = await detector.detect(frame);
        const raw = found[0]?.rawValue?.trim();
        if (!raw) return;
        active = false;
        setPhase("paused");
        setStatus("Badge captured. Looking up lead…");
        stopStream();
        try {
          const lead = await fetchBadgeLead(raw, campaignKey);
          setFields(fieldsFromSdkCapture(raw, lead));
          setStatus("Badge captured.");
        } catch (error) {
          setFields(parseBadgePayload(raw));
          const message = error instanceof Error ? error.message : "Plus Lead capture failed";
          setStatus(`Badge captured. SDK lookup failed: ${message}`);
        }
      } catch {
        // Continue scanning through intermittent detector failures.
      } finally {
        busy = false;
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      active = false;
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
      }
    };
  }, [campaignKey, phase, stopStream]);

  useEffect(() => {
    if (!fields) return;
    setCapture(captureDraftFromBadge(fields));
  }, [fields]);

  const fuzzyQueries = useMemo(() => {
    if (!capture) return [];
    const combinedName = [capture.first_name.trim(), capture.last_name.trim()].filter(Boolean).join(" ");
    const combined = [capture.email.trim(), combinedName, capture.company.trim()].filter(Boolean).join(" ");
    return [...new Set([capture.email.trim(), combinedName, capture.company.trim(), combined].filter(Boolean))];
  }, [capture]);

  const refreshLinkedMatches = useCallback(async () => {
    if (!fuzzyQueries.length) {
      setLinkedMatches([]);
      setSelectedMatchIds([]);
      setSelectedDetails({});
      return;
    }
    setMatching(true);
    setMatchError(null);
    try {
      const responses = await Promise.all(
        fuzzyQueries.map((query) =>
          callTool<{ results?: ContactHit[] }>("contact_search", {
            campaign_key: campaignKey || eventKey,
            viewer: actor,
            query,
            limit: 8,
          }),
        ),
      );
      const map = new Map<string, ContactHit>();
      for (const payload of responses) {
        for (const row of payload.results ?? []) {
          if (!row.contact_id) continue;
          map.set(row.contact_id, row);
        }
      }
      setLinkedMatches([...map.values()]);
      setSelectedMatchIds((prev) => prev.filter((id) => map.has(id)));
    } catch (error) {
      setMatchError(error instanceof Error ? error.message : "Profile link search failed");
    } finally {
      setMatching(false);
    }
  }, [actor, campaignKey, eventKey, fuzzyQueries]);

  useEffect(() => {
    if (!capture) return;
    void refreshLinkedMatches();
  }, [capture, refreshLinkedMatches]);

  useEffect(() => {
    const idsToFetch = selectedMatchIds.filter((id) => !selectedDetails[id]);
    if (!idsToFetch.length) return;
    let cancelled = false;
    void Promise.all(
      idsToFetch.map(async (id) => {
        const contact = await callTool<ContactRecord>("contact_get", { contact_id: id });
        return { id, contact };
      }),
    )
      .then((rows) => {
        if (cancelled) return;
        setSelectedDetails((prev) => {
          const next = { ...prev };
          for (const row of rows) next[row.id] = row.contact;
          return next;
        });
      })
      .catch((error) => {
        if (cancelled) return;
        setMatchError(error instanceof Error ? error.message : "Unable to load selected profiles");
      });
    return () => {
      cancelled = true;
    };
  }, [selectedDetails, selectedMatchIds]);

  async function copyPayload() {
    if (!fields?.raw) return;
    try {
      await navigator.clipboard.writeText(fields.raw);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  async function searchContacts() {
    if (!fields) return;
    const query = badgeSearchQuery(fields);
    if (!query) return;
    setSearching(true);
    setSearchError(null);
    try {
      const data = await callTool<{ results?: ContactHit[] }>("contact_search", {
        campaign_key: campaignKey || eventKey,
        viewer: actor,
        query,
        limit: 8,
      });
      setHits(data.results ?? []);
    } catch (error) {
      setHits(null);
      setSearchError(error instanceof Error ? error.message : "contact_search failed");
    } finally {
      setSearching(false);
    }
  }

  function toggleSelectedMatch(contactId: string) {
    setSelectedMatchIds((prev) =>
      prev.includes(contactId) ? prev.filter((id) => id !== contactId) : [...prev, contactId],
    );
  }

  async function mergeSelectedProfiles() {
    if (!capture || !selectedMatchIds.length) return;
    setMerging(true);
    setSaveError(null);
    try {
      let next = { ...capture };
      for (const contactId of selectedMatchIds) {
        const detail = selectedDetails[contactId];
        const hint = linkedMatches.find((row) => row.contact_id === contactId);
        if (!detail && !hint) continue;
        const candidate = detail ? draftFromContact(detail, hint) : {
          first_name: asText(hint?.first_name),
          last_name: asText(hint?.last_name),
          email: asText(hint?.email),
          phone: asText(hint?.phone),
          title: asText(hint?.title),
          company: asText(hint?.account_name) || asText(hint?.company),
        };
        next = mergeIntoDraft(next, candidate);
      }
      setCapture(next);
      setSaveStatus(`Merged ${selectedMatchIds.length} profile${selectedMatchIds.length === 1 ? "" : "s"} into scanned profile.`);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : "Merge failed");
    } finally {
      setMerging(false);
    }
  }

  async function resolveAccountId(companyName: string, website: string): Promise<string> {
    const normalizedCompany = companyName.trim();
    if (!normalizedCompany) throw new Error("Company is required.");
    const search = await callTool<{ results?: Array<{ account_id: string; name?: string | null }> }>("account_search", {
      event_key: eventKey,
      name: normalizedCompany,
      limit: 5,
    });
    const matches = search.results ?? [];
    const exact =
      matches.find((row) => row.name?.trim().toLowerCase() === normalizedCompany.toLowerCase()) ?? matches[0];
    if (exact?.account_id) return exact.account_id;
    const created = await quickCreateAccount(eventKey, actor.trim(), {
      name: normalizedCompany,
      website: website.trim() || undefined,
    });
    return created.account_id;
  }

  function validateCapture(draft: BadgeCaptureDraft): string | null {
    if (!draft.first_name.trim()) return "First name is required.";
    if (!draft.last_name.trim()) return "Last name is required.";
    if (!draft.company.trim()) return "Company is required.";
    if (!draft.email.trim()) return "Email is required.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(draft.email.trim())) return "Email must be valid.";
    return null;
  }

  async function saveCapture(draft: BadgeCaptureDraft, badge: BadgeFields) {
    const validation = validateCapture(draft);
    if (validation) {
      setSaveError(validation);
      return;
    }

    const who = actor.trim();
    if (!who) {
      setSaveError(
        "Writes require non-empty actor and lineage. A record without provenance cannot be trusted or safely re-run.",
      );
      return;
    }

    setSaving(true);
    setSaveError(null);
    setSaveStatus("Saving contact…");
    try {
      const accountId = await resolveAccountId(draft.company, draft.website);
      const sourceKey = eventKey.trim() || EVENT_KEY;
      const payload: Record<string, unknown> = {
        account_id: accountId,
        email: normalizeEmail(draft.email),
        first_name: draft.first_name.trim(),
        last_name: draft.last_name.trim(),
        title: draft.title.trim() || undefined,
        phone: draft.phone.trim() || undefined,
        consent_basis: "badge_scan",
        source_url: draft.website.trim() || undefined,
        company: draft.company.trim(),
        address_line1: draft.address.trim() || undefined,
        city: draft.city.trim() || undefined,
        state_region: draft.state.trim() || undefined,
        postal_code: draft.postal.trim() || undefined,
        country_raw: draft.country.trim() || undefined,
        product_interest: draft.product_interest,
        event_key: sourceKey,
        event_context: sourceKey,
        attribution: sourceKey,
        actor: who,
        lineage: ratingLineage(),
        source: "badge-scan",
        raw_attributes: {
          source_key: sourceKey,
          channel: CHANNEL,
          eventpromoid: draft.promo_plaud ? [EVENT_PROMO_CODE] : [],
          describes_you: draft.describes_you.trim(),
          marketing_opt_in: draft.marketing_opt_in ? "yes" : "no",
          product_interest: draft.product_interest,
          badge_payload: badge.raw,
        },
      };
      const saved = await callTool<{ contact_id: string }>("contact_upsert", payload);
      const externalId = badge.externalId?.trim() || saved.contact_id;
      const augmentationPayload: Record<string, unknown> = {
        eventpromoid: draft.promo_plaud ? [EVENT_PROMO_CODE] : [],
        channel: CHANNEL,
        source_key: sourceKey,
        external_id: externalId,
        captured_at: new Date().toISOString(),
        first_name: draft.first_name.trim(),
        last_name: draft.last_name.trim(),
        email: normalizeEmail(draft.email),
        phone: draft.phone.trim() || "",
        title: draft.title.trim() || "",
        company: draft.company.trim(),
        website: draft.website.trim() || "",
        address: draft.address.trim() || "",
        city: draft.city.trim() || "",
        state: draft.state.trim() || "",
        postal: draft.postal.trim() || "",
        country: draft.country.trim() || "",
        describes_you: draft.describes_you.trim(),
        marketing_opt_in: draft.marketing_opt_in ? "yes" : "no",
        responses: [
          { question: "Product interest", answers: selectedInterests(draft.product_interest) },
          { question: "Promo", answers: draft.promo_plaud ? [EVENT_PROMO_CODE] : [] },
          { question: "What best describes you", answers: [draft.describes_you.trim()] },
        ],
      };
      setSavedContactId(saved.contact_id);
      setNoteContact({
        contact_id: saved.contact_id,
        account_id: accountId,
        first_name: draft.first_name.trim(),
        last_name: draft.last_name.trim(),
      });
      setSaveStatus("Saved. Assigning code…");
      try {
        const code = await assignBadgeAlphaCode(saved.contact_id);
        setAlphaCode(code);
        setSaveStatus("Saved.");
      } catch (error) {
        setSaveError(error instanceof Error ? error.message : "Alpha code assignment failed");
        setSaveStatus("Contact saved. Code assignment failed.");
      }
      try {
        await queueBadgeAugmentation(augmentationPayload);
      } catch (error) {
        console.error("Badge augmentation enqueue failed", error);
      }
      try {
        const journey = await callTool<{ event_contact_display?: string | null }>("campaign_journey_put", {
          contact_id: saved.contact_id,
          campaign_key: campaignKey,
          actor: who,
          lineage: ratingLineage(),
          activities: {},
        });
        setEventContactDisplay(journey.event_contact_display ?? null);
      } catch (error) {
        console.error("Event contact id assignment failed", error);
        setEventContactDisplay(null);
      }
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : "Badge save failed");
      setSaveStatus(null);
    } finally {
      setSaving(false);
    }
  }

  useEffect(() => {
    if (!fields) return;
    if (autoSaveRaw.current === fields.raw) return;
    autoSaveRaw.current = fields.raw;
    setSavedContactId(null);
    setNoteContact(null);
    setAlphaCode(null);
    setEventContactDisplay(null);
    void saveCapture(captureDraftFromBadge(fields), fields);
  }, [fields]);

  async function refreshAlphaCode() {
    if (!savedContactId) return;
    setRefreshingCode(true);
    setSaveError(null);
    try {
      const code = await fetchBadgeAlphaCode(savedContactId);
      setAlphaCode(code);
      setSaveStatus(code ? "Code loaded from the contact." : "No code is stored on this contact yet.");
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : "Alpha code refresh failed");
    } finally {
      setRefreshingCode(false);
    }
  }

  const canSearch = Boolean(fields?.email || fields?.name);
  const canSave = Boolean(capture) && !saving;
  const selectedRows = selectedMatchIds.map((id) => {
    const hit = linkedMatches.find((row) => row.contact_id === id);
    const detail = selectedDetails[id];
    return {
      contact_id: id,
      first_name: asText(detail?.first_name) || asText(hit?.first_name),
      last_name: asText(detail?.last_name) || asText(hit?.last_name),
      email: asText(detail?.email) || asText(hit?.email),
      phone: asText(detail?.phone) || asText(hit?.phone),
      company: asText(detail?.company) || asText(hit?.account_name) || asText(hit?.company),
    };
  });

  return (
    <CompanyBrowserModal title="Scan badge" onClose={onClose} className="badge-scanner-modal">
      <header className="company-modal-header">
        <div className="company-modal-heading">
          <h2>Scan badge</h2>
          <button type="button" className="secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </header>
      <div className="company-modal-body badge-scanner-body">
        {!fields ? (
          <div className={`badge-scanner-preview${phase === "live" ? " is-live" : ""}`}>
            <video ref={videoRef} className="badge-scanner-video" autoPlay muted playsInline />
            {phase === "live" ? <div className="badge-scanner-viewfinder" /> : null}
          </div>
        ) : null}
        <p className={phase === "error" ? "err" : "muted"}>{status}</p>

        {fields && capture ? (
          <div className="badge-scanner-result">
            <div className="badge-alpha-code">
              <span>Alpha code</span>
              <strong>{alphaCode ?? (saving ? "…" : "—")}</strong>
              <div className="badge-event-contact">
                <span>Event contact</span>
                <strong>{eventContactDisplay ?? (saving ? "…" : "—")}</strong>
              </div>
              <button
                type="button"
                className="secondary"
                disabled={!savedContactId || refreshingCode || saving}
                onClick={() => void refreshAlphaCode()}
              >
                {refreshingCode ? "Refreshing…" : "Refresh"}
              </button>
            </div>
            {saveError ? <p className="err">{saveError}</p> : null}
            {saveStatus ? <p className="muted">{saveStatus}</p> : null}
            <section className="badge-linking-panel">
              <h3>Notes</h3>
              {noteContact && actor.trim() ? (
                <NotesPane row={noteContact} actor={actor.trim()} />
              ) : (
                <p className="muted">Save the badge to take notes on this person.</p>
              )}
            </section>
            <div className="badge-scanner-header-row">
              <div>
                <strong>{[capture.first_name, capture.last_name].filter(Boolean).join(" ") || fields.name || "Scanned profile"}</strong>
                {capture.email ? <p className="muted">{capture.email}</p> : null}
              </div>
              <button type="button" className="secondary" onClick={() => void startCamera()}>
                Scan again
              </button>
            </div>

            <div className="badge-capture-form">
              <div className="contact-form-grid">
                <label className="field">
                  First name
                  <input
                    value={capture.first_name}
                    onChange={(e) => setCapture((prev) => (prev ? { ...prev, first_name: e.target.value } : prev))}
                  />
                </label>
                <label className="field">
                  Last name
                  <input
                    value={capture.last_name}
                    onChange={(e) => setCapture((prev) => (prev ? { ...prev, last_name: e.target.value } : prev))}
                  />
                </label>
                <label className="field">
                  Email
                  <input
                    type="email"
                    value={capture.email}
                    onChange={(e) => setCapture((prev) => (prev ? { ...prev, email: e.target.value } : prev))}
                  />
                </label>
                <label className="field">
                  Phone
                  <input
                    value={capture.phone}
                    onChange={(e) => setCapture((prev) => (prev ? { ...prev, phone: e.target.value } : prev))}
                  />
                </label>
                <label className="field">
                  Title
                  <input
                    value={capture.title}
                    onChange={(e) => setCapture((prev) => (prev ? { ...prev, title: e.target.value } : prev))}
                  />
                </label>
                <label className="field">
                  Company
                  <input
                    value={capture.company}
                    onChange={(e) => setCapture((prev) => (prev ? { ...prev, company: e.target.value } : prev))}
                  />
                </label>
                <label className="field">
                  Website
                  <input
                    value={capture.website}
                    onChange={(e) => setCapture((prev) => (prev ? { ...prev, website: e.target.value } : prev))}
                  />
                </label>
                <label className="field">
                  Address
                  <input
                    value={capture.address}
                    onChange={(e) => setCapture((prev) => (prev ? { ...prev, address: e.target.value } : prev))}
                  />
                </label>
                <label className="field">
                  City
                  <input
                    value={capture.city}
                    onChange={(e) => setCapture((prev) => (prev ? { ...prev, city: e.target.value } : prev))}
                  />
                </label>
                <label className="field">
                  State
                  <input
                    value={capture.state}
                    onChange={(e) => setCapture((prev) => (prev ? { ...prev, state: e.target.value } : prev))}
                  />
                </label>
                <label className="field">
                  Postal
                  <input
                    value={capture.postal}
                    onChange={(e) => setCapture((prev) => (prev ? { ...prev, postal: e.target.value } : prev))}
                  />
                </label>
                <label className="field">
                  Country
                  <input
                    value={capture.country}
                    onChange={(e) => setCapture((prev) => (prev ? { ...prev, country: e.target.value } : prev))}
                  />
                </label>
                <div className="field">
                  <span>Product interest</span>
                  <div className="checkbox-group badge-capture-checks">
                    {PRODUCT_INTEREST_OPTIONS.map(({ key, label }) => (
                      <label key={key} className="field checkbox-field badge-product-option">
                        <input
                          type="checkbox"
                          checked={capture.product_interest[key]}
                          onChange={(e) =>
                            setCapture((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    product_interest: {
                                      ...prev.product_interest,
                                      [key]: e.target.checked,
                                    },
                                  }
                                : prev,
                            )
                          }
                        />
                        {label.toUpperCase()}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="field">
                  <span>Promo</span>
                  <label className="field checkbox-field badge-promo-option">
                    <input
                      type="checkbox"
                      checked={capture.promo_plaud}
                      onChange={(e) => setCapture((prev) => (prev ? { ...prev, promo_plaud: e.target.checked } : prev))}
                    />
                    {EVENT_PROMO_LABEL}
                  </label>
                </div>
                <label className="field">
                  What best describes you
                  <select
                    value={capture.describes_you}
                    onChange={(e) => setCapture((prev) => (prev ? { ...prev, describes_you: e.target.value } : prev))}
                  >
                    {DESCRIBES_YOU_OPTIONS.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="field">
                  <label className="field checkbox-field badge-promo-option">
                    <input
                      type="checkbox"
                      checked={capture.marketing_opt_in}
                      onChange={(e) =>
                        setCapture((prev) => (prev ? { ...prev, marketing_opt_in: e.target.checked } : prev))
                      }
                    />
                    Marketing opt-in
                  </label>
                </div>
              </div>
            </div>

            <section className="badge-linking-panel">
              <div className="badge-linking-header">
                <h3>Profile linking</h3>
                <div className="badge-linking-summary">
                  <span className="muted">{matching ? "Searching…" : `${linkedMatches.length} Matches found`}</span>
                  <button type="button" className="secondary" onClick={() => void refreshLinkedMatches()} disabled={matching}>
                    Refresh
                  </button>
                </div>
              </div>
              {matchError ? <p className="err">{matchError}</p> : null}
              {linkedMatches.length ? (
                <table className="badge-merge-table">
                  <thead>
                    <tr>
                      <th />
                      <th>First</th>
                      <th>Last</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Company</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linkedMatches.map((row) => (
                      <tr key={row.contact_id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedMatchIds.includes(row.contact_id)}
                            onChange={() => toggleSelectedMatch(row.contact_id)}
                          />
                        </td>
                        <td>{row.first_name || "—"}</td>
                        <td>{row.last_name || "—"}</td>
                        <td>{row.email || "—"}</td>
                        <td>{row.phone || "—"}</td>
                        <td>{row.account_name || row.company || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="muted">No linked profile candidates yet.</p>
              )}
            </section>

            {selectedMatchIds.length ? (
              <section className="badge-linking-panel">
                <h3>Merge preview</h3>
                <table className="badge-merge-table">
                  <thead>
                    <tr>
                      <th>Profile</th>
                      <th>First</th>
                      <th>Last</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Company</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>This profile</td>
                      <td>{capture.first_name || "—"}</td>
                      <td>{capture.last_name || "—"}</td>
                      <td>{capture.email || "—"}</td>
                      <td>{capture.phone || "—"}</td>
                      <td>{capture.company || "—"}</td>
                    </tr>
                    {selectedRows.map((row) => (
                      <tr key={row.contact_id}>
                        <td>Other profile</td>
                        <td>{row.first_name || "—"}</td>
                        <td>{row.last_name || "—"}</td>
                        <td>{row.email || "—"}</td>
                        <td>{row.phone || "—"}</td>
                        <td>{row.company || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="badge-scanner-actions">
                  <button type="button" className="secondary" disabled={merging} onClick={() => void mergeSelectedProfiles()}>
                    {merging ? "Merging…" : "Merge selected rows"}
                  </button>
                  {selectedMatchIds.length === 1 && onOpenContact ? (
                    <button
                      type="button"
                      className="ghost"
                      onClick={() => onOpenContact(selectedMatchIds[0])}
                    >
                      Open selected existing profile
                    </button>
                  ) : null}
                </div>
              </section>
            ) : null}

            <div className="badge-scanner-actions">
              <button type="button" className="secondary" onClick={() => void copyPayload()}>
                {copied ? "Copied" : "Copy payload"}
              </button>
              <button
                type="button"
                disabled={!canSave || !capture || !fields}
                onClick={() => capture && fields && void saveCapture(capture, fields)}
              >
                {saving ? "Saving…" : "Save"}
              </button>
              {canSearch ? (
                <button type="button" disabled={searching} onClick={() => void searchContacts()}>
                  {searching ? "Searching…" : "Search contacts"}
                </button>
              ) : null}
            </div>
            <p className="muted">Badge barcode</p>
            <pre className="badge-scanner-raw">{fields.raw}</pre>
          </div>
        ) : null}

        {searchError ? <p className="err">{searchError}</p> : null}
        {hits ? (
          hits.length ? (
            <ul className="badge-scanner-hits">
              {hits.map((row) => (
                <li key={row.contact_id}>
                  <div>
                    <strong>{personName(row)}</strong>
                    <span className="muted">
                      {[row.title, row.account_name || row.company, row.email].filter(Boolean).join(" · ")}
                    </span>
                  </div>
                  <div className="badge-scanner-hit-actions">
                    {row.account_id && onOpenCompany ? (
                      <button
                        type="button"
                        className="secondary"
                        onClick={() => onOpenCompany({ account_id: row.account_id! })}
                      >
                        Open account
                      </button>
                    ) : null}
                    {onOpenContact ? (
                      <button type="button" className="ghost" onClick={() => onOpenContact(row.contact_id)}>
                        Open record
                      </button>
                    ) : null}
                    {onViewInContacts ? (
                      <button
                        type="button"
                        className="ghost"
                        onClick={() => onViewInContacts(badgeSearchQuery(fields ?? { raw: personName(row) }))}
                      >
                        Open in Contacts
                      </button>
                    ) : null}
                    {row.email && marketingCampaignId ? (
                      <button type="button" className="secondary" onClick={() => setSendHit(row)}>
                        Send communication…
                      </button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">No matching contacts.</p>
          )
        ) : null}
      </div>
      {sendHit && marketingCampaignId ? (
        <ContactSendModal
          contactId={sendHit.contact_id}
          email={sendHit.email ?? ""}
          campaignId={marketingCampaignId}
          onClose={() => setSendHit(null)}
        />
      ) : null}
    </CompanyBrowserModal>
  );
}
